import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Experiment, Variation } from '@justgiving/experiments';
import loadable from '@loadable/component';
import i18n from 'i18next';

import {
  FundraisingPageFragment,
  GetStravaMeasurementSystemQuery,
  PageStatus,
  PageType,
  PublishingSource,
  TimelineEntryType,
} from '../../../types/__generated__';
import config from '../../client/config';
import AboutCampaignAndCharitySection from '../../components/AboutCampaignAndCharitySection/AboutCampaignAndCharitySection';
import AboutCauseMini from '../../components/AboutCauseMini/AboutCauseMini';
import { ActivityTotals } from '../../components/ActivityTotals/ActivityTotals';
import { Banner } from '../../components/Banner/Banner';
import { DonateToYourselfModal } from '../../components/DonateToYourselfModal/DonateToYourselfModal';
import { ReadyToGoModal } from '../../components/DonateToYourselfModal/ReadyToGoModal';
import DonationPrompts from '../../components/DonationPrompts/DonationPrompts';
import DonationSummary from '../../components/DonationSummary/DonationSummary';
import EventDetails from '../../components/EventDetails/EventDetails';
import FeatureImage from '../../components/FeatureImage/FeatureImage';
import FundraisingCTA from '../../components/FundraisingCTA/FundraisingCTA';
import FundraisingHelmet from '../../components/Helmet/FundraisingHelmet';
import IsPageOwner from '../../components/IsPageOwner/IsPageOwner';
import IsQRCode from '../../components/IsQRCode/IsQRCode';
import DefaultLayout from '../../components/Layout/DefaultLayout';
import Page from '../../components/Page/Page';
import PageAlert from '../../components/PageAlert/PageAlert';
import PageOwnerAvatar from '../../components/PageOwnerAvatar/PageOwnerAvatar';
import PageOwnerTitle from '../../components/PageOwnerTitle/PageOwnerTitle';
import PageSection from '../../components/PageSection/PageSection';
import { PropositionBar } from '../../components/PropositionBar/PropositionBar';
import Reason from '../../components/Reason/Reason';
import { ShareModal } from '../../components/ShareModal/ShareModal';
import FundraisingSocialButtons from '../../components/SocialButtons/FundraisingSocialButtons';
import Story from '../../components/Story/Story';
import SupportersList from '../../components/SupportersList/SupportersList';
import TeamDetails from '../../components/TeamDetails/TeamDetails';
import getStravaMeasurementSystem from '../../components/Timeline/StravaTimelineEntry/GetStravaMeasurementSystem.gql';
import TotaliserWrapper from '../../components/TotaliserWrapper/TotaliserWrapper';
import { FirstToDonateTotals } from '../../components/Totals/FirstToDonateTotals';
import { Totals } from '../../components/Totals/Totals';
import { Experiments } from '../../constants/experiments';
import PAGE_TYPES from '../../constants/pageTypes';
import { MODAL } from '../../context';
import {
  buildQuery,
  useApp,
  useDonationAmount,
  useFlags,
  useFullPageQuery,
  useListTimelineEntries,
  usePageMeta,
  usePageOwner,
  usePageQuery,
  useShareUrls,
} from '../../hooks';
import { useBypassAmountPageExperimentVariation } from '../../hooks/useBypassAmountPageExperimentVariation';
import { useInView } from '../../hooks/useinView';
import { useScript } from '../../hooks/useScript';
import { useShouldDisplayFirstToDonateTotals } from '../../hooks/useShouldDisplayFirstToDonateTotals';
import { getMeasurementSystemFromSettings } from '../../utils/measurementSystem';
import Fragment from './FundraisingPageFragment.gql';

import styles from './FundraisingPage.scss';

const Prompt = loadable(() => import('../../components/Prompt/Prompt'));
const QRModal = loadable(() => import('../../components/QRModal/QRModal'));
const Editbar = loadable(() => import('../../components/Editbar/Editbar'));
const TimelinePageSection = loadable(() =>
  import('../../components/Timeline/TimelinePageSection/TimelinePageSection'),
);
const FitnessSection = loadable(
  () => import('../../components/FitnessSection/FitnessSection'),
  { resolveComponent: module => module.FitnessSection },
);
const GoToImmersiveGiving = loadable(
  () => import('../../components/GoToImmersiveGiving/GoToImmersiveGiving'),
  {
    resolveComponent: module => module.GoToImmersiveGiving,
  },
);
const TeamAndCompany = loadable(() =>
  import('../../components/TeamAndCompany/TeamAndCompany'),
);
const QualarooLoader = loadable(
  () => import('../../components/QualarooLoader/QualarooLoader'),
  { resolveComponent: module => module.QualarooLoader },
);
const StickySummary = loadable(() =>
  import('../../components/StickySummary/StickySummary'),
);
const EncourageInactivityModal = loadable(() =>
  import('../../components/EncourageInactivityModal/EncourageInactivityModal'),
);

const FundraisingPageQuery = buildQuery(Fragment);

export const FundraisingPage: React.FunctionComponent = () => {
  useScript('//rum-static.pingdom.net/pa-5e3af8dc229e9300080003d1.js', {
    async: true,
  });
  const { inViewRef, inView, rootIsBelowTarget } = useInView();
  const { data } = usePageQuery<FundraisingPageFragment>(FundraisingPageQuery);
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName, pageType } = usePageMeta();
  const { enableOtgImmersiveGiving } = useFlags();

  // AB test: starting_donation_on_frp_with_amount_page
  // Can be removed when experiment has ended
  const bypassAmountPageExperimentVariation = useBypassAmountPageExperimentVariation();
  const {
    defaultDonationAmount,
    setDonationAmount,
    setShowDonationAmountVariant,
  } = useDonationAmount();
  useEffect(() => {
    setShowDonationAmountVariant(bypassAmountPageExperimentVariation);
    if (bypassAmountPageExperimentVariation === 'B') {
      setDonationAmount(defaultDonationAmount);
    }
  }, [
    bypassAmountPageExperimentVariation,
    setDonationAmount,
    defaultDonationAmount,
    setShowDonationAmountVariant,
  ]);

  const shouldDisplayFirstToDonateTotals = useShouldDisplayFirstToDonateTotals();

  const name = data?.owner?.name ?? '';
  const eventGivingGroupId = data?.eventGivingGroupId;
  const totalRelatedCompanies = data?.relationships?.companies.totalCount ?? 0;

  const displayTeamAndCompany =
    totalRelatedCompanies > 0 ||
    data?.relationships?.teams.nodes[0]?.status === PageStatus.Active;

  const adminLink =
    pageType === PageType.OnePage.toLowerCase()
      ? `${config.baseUri}/p/${pageShortName}/admin`
      : `${config.baseUri}/account/your-pages/${pageShortName}?pageId=${eventGivingGroupId}`;

  // B2C_044 - Donate to yourself
  // Can be removed when experiment has ended
  const shareUrls = useShareUrls();
  const { isPageOwner } = usePageOwner();
  const { hideModal, showModal, openModal } = useApp();
  const urlParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const isNewPage = Boolean(urlParams.get('newPage'));

  const isNotOwnerOrNewPage = !isPageOwner || !isNewPage;

  useEffect(() => {
    if (isNotOwnerOrNewPage) {
      return;
    }

    showModal({ modal: MODAL.DONATE_TO_YOURSELF });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // B2C_044 - Donate to yourself - end

  // B2C_040 - Trust Proposition Bar
  // Can be removed when experiment has ended
  const language = i18n?.languages?.length
    ? i18n.languages[0].toLowerCase()
    : null;

  const isNotGbLanguage = language !== 'en-gb';
  // B2C_040 - end

  // B2C_048 - show hide fitness data
  // or added as a non experiment test if successful
  const { data: settingsData } = useFullPageQuery<
    GetStravaMeasurementSystemQuery
  >(getStravaMeasurementSystem);
  const measurementSystem = getMeasurementSystemFromSettings(
    settingsData?.page?.strava?.settings,
  );
  const { data: timelineData } = useListTimelineEntries(measurementSystem);

  // Fundraising pages that have at least 1 manual update
  // AND 1 strava update
  function shouldEnrolIn048() {
    const updates = timelineData?.page?.timeline?.nodes;

    if (!updates || updates.length === 0) return false;

    const latestThreeTimelineUpdates = updates.slice(0, 3);

    const fundraisingUpdates = latestThreeTimelineUpdates.filter(
      update => update.type === TimelineEntryType.Manual,
    );
    const fitnessUpdates = latestThreeTimelineUpdates.filter(
      update => update.publishingSource === PublishingSource.Strava,
    );

    const hasOneOrMoreFundraisingUpdates = fundraisingUpdates.length >= 1;
    const hasOneOrMoreFitnessUpdates = fitnessUpdates.length >= 1;

    return (
      hasOneOrMoreFundraisingUpdates &&
      hasOneOrMoreFitnessUpdates &&
      !isPageOwner
    );
  }

  return (
    <Page>
      {/*B2C_044 - Donate to yourself*/}
      {/*Can be removed when experiment has ended*/}
      <Experiment
        slug={Experiments.b2c_044_donate_to_yourself}
        skipEnrolment={isNotOwnerOrNewPage}
      >
        <Variation name="A">
          <>
            {openModal === MODAL.DONATE_TO_YOURSELF && (
              <ReadyToGoModal onClose={() => hideModal()} />
            )}
          </>
        </Variation>
        <Variation name="B">
          <>
            {openModal === MODAL.DONATE_TO_YOURSELF && (
              <DonateToYourselfModal
                heading={t('fundraising.donateToYourself.modal.heading')}
                description={t(
                  'fundraising.donateToYourself.modal.description',
                )}
                onClose={() => hideModal()}
                donateButtonLabel={t(
                  'fundraising.donateToYourself.modal.donateButton',
                )}
              />
            )}
          </>
        </Variation>
      </Experiment>
      {openModal === MODAL.SHARE && shareUrls && (
        <ShareModal
          heading={
            isPageOwner
              ? t('shareModal.heading.pageOwner')
              : t('shareModal.heading', {
                  pageOwner: data?.owner?.name,
                })
          }
          description={t('shareModal.description')}
          onClose={hideModal}
          shareItems={shareUrls}
        />
      )}
      <IsQRCode>
        <QRModal ownerName={name} />
      </IsQRCode>
      {/*B2C_040 - Trust Proposition Bar*/}
      {/*Can be removed when experiment has ended*/}
      <Experiment
        slug={Experiments.b2c_040_frp_trust_proposition_bar}
        skipEnrolment={isPageOwner || isNewPage || isNotGbLanguage}
      >
        <Variation name="B">
          <PropositionBar />
        </Variation>
      </Experiment>
      <PageAlert />
      <Banner />
      {eventGivingGroupId && (
        <IsPageOwner>
          <Prompt entityId={eventGivingGroupId.toString()} />
        </IsPageOwner>
      )}
      <Editbar adminLink={adminLink} />
      <FundraisingHelmet />
      <DefaultLayout
        slots={{
          featureImage: <FeatureImage enableLightbox />,
          ownerAvatar: <PageOwnerAvatar />,
          titles: <PageOwnerTitle />,
          totals: (
            <>
              <TotaliserWrapper />
              {shouldDisplayFirstToDonateTotals ? (
                <FirstToDonateTotals />
              ) : (
                <Totals />
              )}
            </>
          ),
          callsToAction: (
            <div ref={inViewRef} className={styles.ctaWrapper}>
              <FundraisingCTA />
            </div>
          ),
          titlesSupplementary: (
            <div className="jg-space-mbxs jg-space-mbsm@xl jg-space-mbn jg-space-mtxs jg-space-mtsm@xl">
              <EventDetails />
              <Reason />
              <TeamDetails
                showCreateTeamCTA={pageType !== PAGE_TYPES.ONE_PAGE}
              />
            </div>
          ),
          prompts: <DonationPrompts />,
          causeMini: <AboutCauseMini />,
          main: (
            <>
              <PageSection
                title={t('story.title')}
                className="jg-pages-fundraising__story"
                hasBorder={true}
              >
                <Story />
                <h3 className="jg-text--center">
                  {t('story.shareTitle', 'Share this story')}
                </h3>
                <FundraisingSocialButtons />
              </PageSection>
              {/*// B2C_048 - show hide fitness data*/}
              {/*// Can be removed when experiment has ended*/}
              {/*// or added as a non experiment test if successful*/}
              <Experiment
                slug={Experiments.b2c_048_show_hide_fitness_data}
                skipEnrolment={!shouldEnrolIn048()}
              >
                <Variation name="A">
                  <TimelinePageSection />
                </Variation>
                <Variation name="B">
                  <TimelinePageSection entryType={TimelineEntryType.Manual} />
                </Variation>
                <Variation name="C">
                  <TimelinePageSection entryType={TimelineEntryType.Fitness} />
                </Variation>
              </Experiment>
            </>
          ),
          aside: (
            <>
              {enableOtgImmersiveGiving &&
                data?.relationships?.campaigns.nodes[0]?.slug ===
                  'make-a-refugee-shelter-a-home' && <GoToImmersiveGiving />}
              <IsPageOwner>
                <PageSection>
                  <FitnessSection />
                </PageSection>
              </IsPageOwner>
              <Experiment
                slug={Experiments.b2c_048_show_hide_fitness_data}
                skipEnrolment={!shouldEnrolIn048()}
              >
                <Variation name="A">
                  <ActivityTotals />
                </Variation>
                <Variation name="C">
                  <ActivityTotals />
                </Variation>
              </Experiment>
              <SupportersList
                noSupportersMessage={t(
                  'fundraising.noSupportersMessage',
                  `Become {{name}}'s first supporter by making a donation`,

                  { replace: { name } },
                )}
                displayDonateButton
                donateButtonExperiments={[
                  // B2C_041 - Donation Prompts Part 2
                  // Remove this experiment and successType when removing this experiment
                  Experiments.b2c_041_fundraising_page_donation_prompts_2,
                  // B2C_045 - Donate button color
                  // Can be removed when experiment has ended
                  Experiments.b2c_045_donate_button_color,
                  // B2C_040 - Proposition Bar
                  // Remove this experiment when complete
                  Experiments.b2c_040_frp_trust_proposition_bar,
                ]}
              />
            </>
          ),
          teamAndCompany: displayTeamAndCompany && <TeamAndCompany />,
          contentFooterPrimary: <AboutCampaignAndCharitySection />,
          contentFooterSecondary: (
            <DonationSummary
              fieldsToShow={['onlineAmount', 'offlineAmount', 'textDonations']}
            />
          ),
        }}
      />
      {!inView && rootIsBelowTarget && (
        <StickySummary
          donateButtonExperiments={[
            // B2C_041 - Donation Prompts Part 2
            // Remove this experiment and success type when removing this experiment
            Experiments.b2c_041_fundraising_page_donation_prompts_2,
            // B2C_045 - Donate button color
            // Can be removed when experiment has ended
            Experiments.b2c_045_donate_button_color,
            // B2C_040 - Proposition Bar
            // Remove this experiment when complete
            Experiments.b2c_040_frp_trust_proposition_bar,
            // B2C_044 - donate to yourself
            // Remove this experiment when complete
            Experiments.b2c_044_donate_to_yourself,
            // B2C_048 - show hide fitness data
            // Can be removed when experiment has ended
            // or added as a non experiment test if successful
            Experiments.b2c_048_show_hide_fitness_data,
          ]}
        />
      )}
      <EncourageInactivityModal />
      <QualarooLoader />
    </Page>
  );
};

FundraisingPage.displayName = 'FundraisingPage';

export default FundraisingPage;
